import React from 'react'
import { NextPage } from 'next'
import { toNameUpper } from '../../components/utils/string-utils'
import Head from 'next/head'
import { FAQSection } from '../../components/CarPage/FAQSection'
import { WarningSignsSoundsSection } from '../../components/CarPage/WarningSignsSoundsSection'
import { SkipTheShopSection } from '../../components/CarPage/SkipTheShopSection'
import { BetterServiceForLess } from '../../components/CarPage/BetterServiceForLess'
import { WorkAroundYouSection } from '../../components/CarPage/WorkAroundYouSection'
import { RelatedBlogPosts } from '../../components/RelatedBlogPosts'
import { paramCase } from 'param-case'
import Layout from '../../components/Layout'
import { TitleSection } from '../../components/LandingPage/TitleSection'
import { FeatureBySection } from '../../components/FeatureBySection'
import { HowItWorksSection } from '../../components/LandingPage/HowItWorksSection'
import { HappinessGuaranteeSection } from '../../components/HappinessGuaranteeSection'
import { BrakeRepairNearYouSection } from '../../components/LandingPage/BrakeRepairNearYouSection'
import Link from 'next/link'
import { getCar, getCars, getPosts } from '../../asyncLoaders/buttercms'
import { getReviewIODataFromNest } from '../../asyncLoaders/nest'

import site from '../../../public/img/get-quote.svg'
import calendar from '../../../public/img/calendar-icon.svg'
import brakes from '../../../public/img/brakes.svg'

type CarPostTemplateProps = {
  possessiveName: string
  makeShortName: string
  rating: string
  reviewCount: number
  tags: string[]
  carImage: string
  backgroundImage: string
  modelList: string
  posts: any[]
}

export const getStaticPaths = async () => {
  const carsContent = await getCars()
  return {
    paths: carsContent.map((car: any) => ({
      params: { slug: paramCase(car.slug) },
    })),
    fallback: false,
  }
}

export const getStaticProps = async ({ params }) => {
  const slug = params.slug
  const { rating, reviewCount } = await getReviewIODataFromNest()
  const [car, posts] = await Promise.all([getCar(slug), getPosts()])
  if (!car) {
    return {
      notFound: true, //https://github.com/vercel/next.js/discussions/10960
    }
  }
  return {
    props: {
      car,
      posts,
      rating,
      reviewCount,
    },
  }
}

export const CarPostTemplate: React.FC<CarPostTemplateProps> = ({
  possessiveName,
  makeShortName,
  carImage,
  backgroundImage,
  posts,
  rating,
  reviewCount,
}) => {
  const formalShortName = toNameUpper(makeShortName)
  const formalLongName = toNameUpper(makeShortName)
  const formalPossesive = toNameUpper(possessiveName)
  return (
    <>
      <TitleSection
        reviewCount={reviewCount}
        rating={rating}
        title={`Top-Rated, Affordable ${formalShortName} Brake Repair Services Delivered To You`}
        image={backgroundImage}
        callToActionButtonText={`Get a Free Brake Repair Estimate For Your ${makeShortName}`}
        subheading={
          <>
            Schedule top-rated {makeShortName} brake service from our mobile brake technicians at your office, home or
            apartment — all backed by our <Link href={'/warranty'}>NuBrakes Guarantee</Link> and crazy good customer
            service.
          </>
        }
      />
      <FeatureBySection />
      <HowItWorksSection
        title={`How Our Mobile ${formalLongName} Brake Service Works`}
        description={`The easiest way to service your ${formalPossesive} brakes for less`}
        steps={[
          {
            title: 'Get Your Free Repair Quote',
            description:
              'Let us know what’s going on with your brakes and we’ll send you a free, no obligation repair quote for your vehicle.',
            image: site,
          },
          {
            title: 'Select Your Repair Date',
            description:
              'Select a repair date and time that works around your schedule. We pride ourselves on making it simple for you.',
            image: calendar,
          },
          {
            title: 'Enjoy Safer, Better Driving',
            description:
              'Our brake technicians will ensure that your brakes are ready to hit the road and get you back to driving worry-free!',
            image: brakes,
          },
        ]}
      />
      <WorkAroundYouSection
        title={`${formalLongName} Brake Repair That Works Around Your Schedule & Budget`}
        image={carImage}
        description={
          <>
            Driving a high-performance car like a {formalLongName} means {"you'll"} want to keep its brakes maintained
            frequently, but with that comes unnecessary expenses. Our mobile brake technicians have over 15 years of
            experience handling {formalShortName} brakes and mechanical needs, so they know exactly how to service your
            {formalShortName} and get it back on the road. Plus, all of our work is backed by our{' '}
            <Link href={'/warranty'}>NuBrakes Guarantee</Link> to ensure your brakes perform as they should each and
            every day.
          </>
        }
      />
      <BetterServiceForLess
        title={`Better ${formalShortName} Brake Service For Less.`}
        description={`Owning and maintaining a ${formalShortName} can be expensive, but there are ways to ensure that you're not on the losing end of a repair bill. At NuBrakes, we're transparent about our pricing and we try to provide all ${formalShortName} owners with the peace of mind that they're getting a fair and honest brake service price using premium parts that last. As with every brake service, our brake technicians will diagnose and replace brake pads, rotors and calipers only when absolutely necessary to the longevity and safety of your vehicle.`}
        formalModelName={formalShortName}
      />
      <SkipTheShopSection
        title={`Skip The Shop, We Bring Top-Rated ${formalShortName} Brake Services To Your Work or Home.`}
        description={`Stop searching for the best ${formalShortName} brake service near you and let it come to you. Our mobile ${formalShortName} brake service technicians provide top-rated, fully-warranted brake services for your ${formalShortName} right where you're parked at your office, home or apartment. Why waste half a day and pay more at the local brake shop or dealer when you could sit in the comfort of your office or home and receive premium ${formalShortName} brake service for less.`}
        buttonText={`Get a Free Brake Repair Estimate For Your ${formalShortName}`}
      />
      <WarningSignsSoundsSection
        title={`${formalShortName} Brake Problems & Symptoms`}
        subheading={`Warning Signs & Sounds`}
        formalMakeName={formalShortName}
      />
      <HappinessGuaranteeSection
        title={'We have a 100% NuBrakes Happiness Guarantee!'}
        description={
          "We want to make the brake repair process simple and frustration-free. If you're not happy with your repair or replacement, we'll do everything we can to fix it. Call or message us any day, any time, and we'll do our best to fix whatever issue you're having."
        }
      />

      <FAQSection
        title={`Frequently Asked ${formalShortName} Brake Repair Questions`}
        description={`Learn more about our ${formalShortName} brake service.`}
        faqs={[
          {
            question: `How is NuBrakes different from other ${formalShortName} brake repairs near me?`,
            answer: `NuBrakes provides a modern approach to brake repair and replacements by providing an on-demand style service that comes to you. Unlike traditional ${formalShortName} brake service shops that require to you to wait in the lobby while the brake repair is being done, use poor quality parts, and lack transparent pricing; NuBrakes will start by sending you a transparent quote, upon your approval, a NuBrakes ASE-certified technician will come to you with everything they need to repair your brakes. We want to save you time and provide you with a trustworthy and transparent diagnosis and repair quote for your ${formalShortName}. To request a quote, simply click here, fill out your vehicle information and brake symptoms, and we’ll send you a quote for your ${formalShortName} brake service in under an hour.`,
          },
          {
            question: `Are your ${formalShortName} brake service technicians ASE-certified?`,
            answer: `Absolutely. Every NuBrakes ${formalShortName} brake repair technician has years of industry experience and has been certified by The National Institute for Automotive Service Excellence (ASE). Our technicians have a depth of knowledge in all forms of vehicle repair, but brake repair is our core service and specialty.`,
          },
          {
            question: `Why should I choose mobile brake repair?`,
            answer: `We’re a firm believer that your time, energy, and a trustworthy service provider is much more valuable than spending your Saturday afternoon at the brake repair shop. We’ve built the NuBrakes service so that you can efficiently receive a quote and schedule a mobile brake repair service that comes to you, with just a few clicks. There’s not enough time in the day and we think you should spend more time doing what you love and waiting at the brake repair shop is not it. Not to mention, many brake repair shops will quote you more than you need to spend, and often suggest you repair or replace brake parts when it is not necessary. What’s better than saving time and money?`,
          },
          {
            question: `What type of brake pads does NuBrakes use?`,
            answer: (
              <>
                High-quality parts are of the utmost importance when repairing your brakes. At NuBrakes, most frequently
                we are using professional ceramic or semi-metallic brake pads. These parts offer exceptional performance
                and are backed by our <Link href={'/warranty'}>NuBrakes Guarantee</Link>.
              </>
            ),
          },
          {
            question: `How long does it take to repair or replace my brakes?`,
            answer: `On average, your mobile brake repair service will take 45-90 minutes, with the average appointment lasting around an hour. If you have any time constraints, we’re happy to coordinate around your schedule.`,
          },
          {
            question: `What should I expect during my brake repair appointment?`,
            answer: `After you request a free brake repair quote, you’ll receive an email within 1 hour that will outline our best guess (based on the symptoms you provided) for the repairs and estimated cost. On the day of your appointment, a NuBrakes technician will arrive with all the tools they need to evaluate your brakes in person. Following the in-person evaluation, your technician will provide you with a formal repair quote. Upon your approval, the technician will complete the brake repair procedure.`,
          },
          {
            question: `Do you service all ${formalShortName} models?`,
            answer: `Yes, we’re able to provide brake service on nearly all ${formalShortName} models and trims! All of our mobile brake replacement technicians have all of the necessary tools and experience to repair your brakes no matter the model/year. So whether you’re looking for service on a 328i or an M-class, we can take care of your brake service needs.`,
          },
          {
            question: `Do you offer a warranty on the brake repairs?`,
            answer: (
              <>
                Yes, your NuBrakes mobile brake repair is backed by our{' '}
                <Link href={'/warranty'}>NuBrakes Guarantee</Link>.
              </>
            ),
          },
        ]}
      />
      <RelatedBlogPosts
        posts={posts}
        title={`${formalShortName} Brake Repair Service Posts & News`}
        description={`Everything ${formalShortName} & Brakes.`}
      />
      <BrakeRepairNearYouSection
        title={`We’re the closest ${formalShortName} brake service near you. Skip waiting in line at the repair shop, we come to you!`}
        cities={[
          { cityName: 'Austin, TX', linkUrl: '/cities/austin-brake-repair' },
          {
            cityName: 'San Antonio, TX',
            linkUrl: '/cities/san-antonio-brake-repair',
          },
          {
            cityName: 'Forth Worth, TX',
            linkUrl: '/cities/fort-worth-brake-repair',
          },
          { cityName: 'Dallas, TX', linkUrl: '/cities/dallas-brake-repair' },
          {
            cityName: 'League City, TX',
            linkUrl: '/cities/league-city-brake-repair',
          },
          { cityName: 'Houston, TX', linkUrl: '/cities/houston-brake-repair' },
          {
            cityName: 'Pflugerville, TX',
            linkUrl: '/cities/pflugerville-brake-repair',
          },
          { cityName: 'Atlanta, GA', linkUrl: '/cities/atlanta-brake-repair' },
          { cityName: 'Nashville, TN', linkUrl: '/cities/nashville-brake-repair' },
          { cityName: 'Tampa, FL', linkUrl: '/cities/tampa-brake-repair' },
          { cityName: 'Orlando, FL', linkUrl: '/cities/orlando-brake-repair' },
          { cityName: 'Jacksonville, FL', linkUrl: '/cities/jacksonville-brake-repair' },
          { cityName: 'Miami, FL', linkUrl: '/cities/miami-brake-repair' },
          { cityName: 'Chicago, IL', linkUrl: '/cities/chicago-brake-repair' },
        ]}
      />
    </>
  )
}

type CarData = {
  possessiveName: string
  makeShortName: string
  tags: string[]
  makeLogo: string
  makeLongName: string
  carImage: string
  backgroundImage: string
  modelList: string
}

const CarsPost: NextPage<{
  car: CarData
  cars: CarData[]
  posts: any[]
  rating: string
  reviewCount: number
}> = ({ car, posts, reviewCount, rating }) => {
  const { possessiveName, makeShortName, tags, carImage, backgroundImage, modelList } = car

  return (
    <Layout title={`${toNameUpper(makeShortName)} Brake Repair - NuBrakes`}>
      <Head>
        <meta
          name="description"
          content={`Schedule brake repair services for your ${toNameUpper(makeShortName)} from NuBrakes`}
        />
      </Head>
      <CarPostTemplate
        backgroundImage={backgroundImage}
        carImage={carImage}
        rating={rating}
        reviewCount={reviewCount}
        makeShortName={makeShortName}
        possessiveName={possessiveName}
        modelList={modelList}
        tags={tags}
        posts={posts}
      />
    </Layout>
  )
}

export default CarsPost
